import benchmarkUtility from '@sparta.sales.utilities/benchmark-utility/dist/es6.js';

/**
 * @exports
 * @class Siema
 */
class Siema {
  /**
   * @description Create a Siema.
   * @method constructor
   * @param {Object} options - Optional settings object.
   * @returns {void}
   */
  constructor(options) {
    // Merge defaults with user's settings
    this.config = Siema.mergeSettings(options);

    // Create global references
    this.selector = typeof this.config.selector === 'string' ? document.querySelector(this.config.selector) : this.config.selector;
    this.selectorWidth = this.selector.offsetWidth;
    this.innerElements = [].slice.call(this.selector.children);
    this.currentSlide = this.config.startIndex;
    this.transformProperty = Siema.webkitOrNot();
    this.originalElements = this.innerElements;
    this.leftLimit = 0;
    this.rightLimit = 0;

    // Bind all event handlers for referencability
    ['resizeHandler', 'touchstartHandler', 'touchendHandler', 'touchmoveHandler', 'mousedownHandler', 'mouseupHandler', 'mouseleaveHandler', 'mousemoveHandler'].forEach(method => {
      this[method] = this[method].bind(this);
    });
    if (spaParams.siteParams.jsTimer) {
      benchmarkUtility.init(this);
    }
  }

  /**
   * @description Overrides default settings with custom ones.
   * @method mergeSettings
   * @param {Object} options - Optional settings object.
   * @returns {Object} - Custom Siema settings.
   */
  static mergeSettings(options) {
    return Object.assign({
      selector: '.siema',
      duration: 200,
      easing: 'ease-out',
      perPage: 1,
      startIndex: 0,
      draggable: true,
      threshold: 20,
      loop: false,
      onInit: () => {},
      onChange: () => {}
    }, options);
  }

  /**
   * @description Determine if browser supports unprefixed transform property.
   * @method webkitOrNot
   * @returns {string} - Transform property supported by client.
   */
  static webkitOrNot() {
    const {
      style
    } = document.documentElement;
    if (typeof style.transform === 'string') {
      return 'transform';
    }
    return 'WebkitTransform';
  }

  /**
   * Builds the markup and attaches listeners to required events.
   *
   * @param {Element} newElements - optional passed elements for innerElements assignment
   * @returns {void}
   */
  init(newElements) {
    // Resize element on window resize
    window.addEventListener('resize', this.resizeHandler);

    // If element is draggable / swipable, add event handlers
    if (this.config.draggable) {
      // Keep track pointer hold and dragging distance
      this.pointerDown = false;
      this.drag = {
        startX: 0,
        endX: 0,
        startY: 0,
        letItGo: null
      };

      // Touch events
      this.selector.addEventListener('touchstart', this.touchstartHandler);
      this.selector.addEventListener('touchend', this.touchendHandler);
      this.selector.addEventListener('touchmove', this.touchmoveHandler, {
        passive: true
      });

      // Mouse events
      this.selector.addEventListener('mousedown', this.mousedownHandler);
      this.selector.addEventListener('mouseup', this.mouseupHandler);
      this.selector.addEventListener('mouseleave', this.mouseleaveHandler);
      this.selector.addEventListener('mousemove', this.mousemoveHandler);
    }
    if (this.selector === null) {
      throw new Error('Something wrong with your selector 😭');
    }

    // update perPage number dependable of user value
    this.resolveSlidesNumber();

    // hide everything out of selector's boundaries
    this.selector.style.overflow = 'hidden';

    // Create frame and apply styling
    this.sliderFrame = document.createElement('div');
    this.sliderFrame.style.width = `${this.selectorWidth / this.perPage * this.innerElements.length}px`;
    this.sliderFrame.style.webkitTransition = `all ${this.config.duration}ms ${this.config.easing}`;
    this.sliderFrame.style.transition = `all ${this.config.duration}ms ${this.config.easing}`;
    if (this.config.draggable) {
      this.selector.style.cursor = '-webkit-grab';
    }

    // innerElements may have been cleared if user went to desktop
    // and destroyed the carousel - this will restore
    this.innerElements = newElements || this.originalElements;

    // slice first and last 3 elements to clone
    const firstThree = this.innerElements.slice(0, 3);
    const lastThree = this.innerElements.slice(this.innerElements.length - 3);
    const firstThreeArray = [];
    const lastThreeArray = [];

    // clone the first and last three elements
    firstThree.forEach((e, cloneIndex) => {
      const clonedNode = e.cloneNode(true);
      clonedNode.classList.add('clone');
      clonedNode.classList.add('first');
      Array.from(clonedNode.querySelectorAll('a')).forEach((link, linkIndex) => {
        // eslint-disable-next-line no-param-reassign
        link.id += `-cf-${cloneIndex}-${linkIndex}`;
      });
      firstThreeArray.push(clonedNode);
    });
    lastThree.forEach((e, cloneIndex) => {
      const clonedNode = e.cloneNode(true);
      clonedNode.classList.add('clone');
      clonedNode.classList.add('last');
      Array.from(clonedNode.querySelectorAll('a')).forEach((link, linkIndex) => {
        // eslint-disable-next-line no-param-reassign
        link.id += `-cl-${cloneIndex}-${linkIndex}`;
      });
      lastThreeArray.push(clonedNode);
    });

    // add the cloned elements to this.innerElements before the slider is appended to DOM
    this.innerElements = this.innerElements.concat(firstThreeArray);
    this.innerElements = lastThreeArray.concat(this.innerElements);

    // set the rightLimit now that we have all elements created for slider
    this.rightLimit = this.innerElements.length - this.config.startIndex;

    // Create a document fragment to put slides into it
    const docFragment = document.createDocumentFragment();

    // Loop through the slides, add styling and add them to document fragment
    for (let i = 0; i < this.innerElements.length; i += 1) {
      const elementContainer = document.createElement('div');
      elementContainer.classList.add('slide-holder');
      elementContainer.style.cssFloat = 'left';
      elementContainer.style.float = 'left';
      elementContainer.style.width = `${100 / this.innerElements.length}%`;
      elementContainer.appendChild(this.innerElements[i]);
      docFragment.appendChild(elementContainer);
    }

    // Add fragment to the frame
    this.sliderFrame.appendChild(docFragment);

    // Clear selector (just in case something is there) and insert a frame
    this.selector.innerHTML = '';
    this.selector.appendChild(this.sliderFrame);

    // Go to currently active slide after initial build
    this.slideToCurrent();
    this.config.onInit.call(this);
    // need this to show correct dimenstions of slides
    this.resizeHandler();
  }

  /**
   * @description Determinates slides number acordingly to clients viewport.
   * @method resolveSlidesNumber
   * @returns {void}
   */
  resolveSlidesNumber() {
    if (typeof this.config.perPage === 'number') {
      this.perPage = this.config.perPage;
    } else if (typeof this.config.perPage === 'object') {
      this.perPage = 1;
      Object.keys(this.config.perPage).forEach(viewport => {
        if (window.innerWidth >= viewport) {
          this.perPage = this.config.perPage[viewport];
        }
      });
    }
  }

  /**
   * @description Updates animation duration on the slider frame
   * @method updateSlideDuration
   * @param { number } value - the slide duration
   * @returns {void}
   */
  updateSlideDuration(value) {
    this.config.duration = value;
    if (this.sliderFrame) {
      this.sliderFrame.style.webkitTransition = `all ${this.config.duration}ms ${this.config.easing}`;
    }
  }

  /**
   * @description Go to previous slide.
   * @method prev
   * @param {number} [howManySlides=1] - How many items to slide backward.
   * @param {function} callback - Optional callback function.
   * @returns {void}
   */
  prev(howManySlides = 1, callback) {
    if (this.innerElements.length <= this.perPage) {
      return;
    }
    const beforeChange = this.currentSlide;
    if (this.currentSlide === 0 && this.config.loop) {
      this.currentSlide = this.innerElements.length - this.perPage;
    } else {
      this.currentSlide = Math.max(this.currentSlide - howManySlides, 0);
    }
    if (beforeChange !== this.currentSlide) {
      this.slideToCurrent();
      this.config.onChange.call(this);
      if (callback) {
        callback.call(this);
      }
    }
  }

  /**
   * @description Go to next slide.
   * @method next
   * @param {number} [howManySlides=1] - How many items to slide forward.
   * @param {function} callback - Optional callback function.
   * @returns {void}
   */
  next(howManySlides = 1, callback) {
    if (this.innerElements.length <= this.perPage) {
      return;
    }
    const beforeChange = this.currentSlide;
    if (this.currentSlide === this.innerElements.length - this.perPage && this.config.loop) {
      this.currentSlide = 0;
    } else {
      this.currentSlide = Math.min(this.currentSlide + howManySlides, this.innerElements.length - this.perPage);
    }
    if (beforeChange !== this.currentSlide) {
      this.slideToCurrent();
      this.config.onChange.call(this);
      if (callback) {
        callback.call(this);
      }
    }
  }

  /**
   * @description Go to slide with particular index
   * @method goTo
   * @param {number} index - Item index to slide to.
   * @param {function} callback - Optional callback function.
   * @returns {void}
   */
  goTo(index, callback) {
    if (this.innerElements.length <= this.perPage) {
      return;
    }
    this.currentSlide = Math.min(Math.max(index, 0), this.innerElements.length - this.perPage);
    this.slideToCurrent();
    if (callback) {
      callback.call(this);
    }
  }

  /**
   * @description Moves sliders frame to position of currently active slide
   * @method slideToCurrent
   * @returns {void}
   */
  slideToCurrent() {
    // reset the translate properties to default before each slide action
    this.sliderFrame.style[this.transformProperty] = `translate3d(-${this.currentSlide * (this.selectorWidth / this.perPage)}px, 0, 0)`;
    if (this.currentSlide === this.leftLimit) {
      // we are at the left end of the slider and need to loop to the end
      // loopedStartIndex is the slide index of where we should focus when we
      // go the left far enough to restart the infinite loop
      const loopedStartIndex = this.innerElements.length / 2;
      setTimeout(() => {
        // logic to move slides to correct place after reaching cloned
        // - currentSlide will be different depending if index is odd or even
        if (loopedStartIndex % 2 === 0) {
          this.currentSlide = loopedStartIndex;
        } else {
          this.currentSlide = loopedStartIndex - 1;
        }
        this.updateFrame();
      }, this.config.duration);
    }
    if (this.currentSlide === this.rightLimit) {
      // we are at the right end of the slider and need to loop back to the start
      setTimeout(() => {
        this.currentSlide = this.config.startIndex; // focus back to startIndex
        this.updateFrame();
      }, this.config.duration);
    }

    // update tabIndex and aria-attributes for visible and hidden slide elements
    this.updateSliderADA();
  }

  /**
   * @description Set the correct aria-attributes and tabIndex for frames in slider
   * @method updateSliderADA
   * @returns {void}
   */
  updateSliderADA() {
    // find elements that are currently showing in slider - these are activeElements
    const activeElements = this.innerElements.slice(this.currentSlide, this.currentSlide + this.perPage);
    // iterate through all slider elements - if slide is visible, it is active
    for (let i = 0, {
        length
      } = this.innerElements; i < length; i += 1) {
      if (activeElements.indexOf(this.innerElements[i]) > -1) {
        // this.innerElements[i].tabIndex = 0;
        this.innerElements[i].parentElement.setAttribute('aria-hidden', false);
        // update tabIndex for links within the active slider elements
        Array.from(this.innerElements[i].querySelectorAll('a')).forEach(el => {
          Object.assign(el, {
            tabIndex: 0
          });
        });
      } else {
        // this.innerElements[i].tabIndex = -1;
        this.innerElements[i].parentElement.setAttribute('aria-hidden', true);
        // update tabIndex for links that are in non-active slides so that user can't tab to them
        Array.from(this.innerElements[i].querySelectorAll('a')).forEach(el => {
          Object.assign(el, {
            tabIndex: -1
          });
        });
      }
    }
  }

  /**
   * @description Recalculate drag /swipe event and reposition the frame of a slider
   * @method updateAfterDrag
   * @returns {void}
   */
  updateAfterDrag() {
    const movement = this.drag.endX - this.drag.startX;
    const movementDistance = Math.abs(movement);
    const howManySliderToSlide = Math.ceil(movementDistance / (this.selectorWidth / this.perPage));
    if (movement > 0 && movementDistance > this.config.threshold && this.innerElements.length > this.perPage) {
      this.prev(howManySliderToSlide);
    } else if (movement < 0 && movementDistance > this.config.threshold && this.innerElements.length > this.perPage) {
      this.next(howManySliderToSlide);
    }
    this.slideToCurrent();
  }

  /**
   * @description When window resizes, resize slider components as well
   * @method resizeHandler
   * @returns {void}
   */
  resizeHandler() {
    // update perPage number dependable of user value
    this.resolveSlidesNumber();
    this.selectorWidth = this.selector.offsetWidth;
    this.sliderFrame.style.width = `${this.selectorWidth / this.perPage * this.innerElements.length}px`;
    this.slideToCurrent();
  }

  /**
   * @description Clear drag after touchend and mouseup event
   * @method clearDrag
   * @returns {void}
   */
  clearDrag() {
    this.drag = {
      startX: 0,
      endX: 0,
      startY: 0,
      letItGo: null
    };
  }

  /**
   * @description touchstart event handler
   * @method touchstartHandler
   * @param {Event} e - event to handle
   * @returns {void}
   */
  touchstartHandler(e) {
    e.stopPropagation();
    this.pointerDown = true;
    this.drag.startX = e.touches[0].pageX;
    this.drag.startY = e.touches[0].pageY;
  }

  /**
   * @description touchend event handler
   * @method touchendHandler
   * @param {Event} e - event to handle
   * @returns {void}
   */
  touchendHandler(e) {
    e.stopPropagation();
    this.pointerDown = false;
    this.sliderFrame.style.webkitTransition = `all ${this.config.duration}ms ${this.config.easing}`;
    this.sliderFrame.style.transition = `all ${this.config.duration}ms ${this.config.easing}`;
    if (this.drag.endX) {
      this.updateAfterDrag();
    }
    this.clearDrag();
  }

  /**
   * @description touchmove event handler
   * @method touchmoveHandler
   * @param {Event} e - event to hadnle
   * @returns {void}
   */
  touchmoveHandler(e) {
    e.stopPropagation();
    if (this.drag.letItGo === null) {
      this.drag.letItGo = Math.abs(this.drag.startY - e.touches[0].pageY) < Math.abs(this.drag.startX - e.touches[0].pageX);
    }
    if (this.pointerDown && this.drag.letItGo) {
      this.drag.endX = e.touches[0].pageX;
      this.sliderFrame.style.webkitTransition = `all 0ms ${this.config.easing}`;
      this.sliderFrame.style.transition = `all 0ms ${this.config.easing}`;
      this.sliderFrame.style[this.transformProperty] = `translate3d(${(this.currentSlide * (this.selectorWidth / this.perPage) + (this.drag.startX - this.drag.endX)) * -1}px, 0, 0)`;
    }
  }

  /**
   * @description mousedown event handler
   * @method mousedownHandler
   * @param {Event} e - event to handle
   * @returns {void}
   */
  mousedownHandler(e) {
    e.preventDefault();
    e.stopPropagation();
    this.pointerDown = true;
    this.drag.startX = e.pageX;
  }

  /**
   * @description mouseup event handler
   * @method mouseupHandler
   * @param {Event} e - event to handle
   * @returns {void}
   */
  mouseupHandler(e) {
    e.stopPropagation();
    this.pointerDown = false;
    this.selector.style.cursor = '-webkit-grab';
    this.sliderFrame.style.webkitTransition = `all ${this.config.duration}ms ${this.config.easing}`;
    this.sliderFrame.style.transition = `all ${this.config.duration}ms ${this.config.easing}`;
    if (this.drag.endX) {
      this.updateAfterDrag();
    }
    this.clearDrag();
  }

  /**
   * @description mousemove event handler
   * @method mousemoveHandler
   * @param {Event} e - event to handle
   * @returns {void}
   */
  mousemoveHandler(e) {
    e.preventDefault();
    if (this.pointerDown) {
      this.drag.endX = e.pageX;
      this.selector.style.cursor = '-webkit-grabbing';
      this.sliderFrame.style.webkitTransition = `all 0ms ${this.config.easing}`;
      this.sliderFrame.style.transition = `all 0ms ${this.config.easing}`;
      this.sliderFrame.style[this.transformProperty] = `translate3d(${(this.currentSlide * (this.selectorWidth / this.perPage) + (this.drag.startX - this.drag.endX)) * -1}px, 0, 0)`;
    }
  }

  /**
   * @description mouseleave event handler
   * @method mouseleaveHandler
   * @param {Event} e - event to handle
   * @returns {void}
   */
  mouseleaveHandler(e) {
    if (this.pointerDown) {
      this.pointerDown = false;
      this.selector.style.cursor = '-webkit-grab';
      this.drag.endX = e.pageX;
      this.sliderFrame.style.webkitTransition = `all ${this.config.duration}ms ${this.config.easing}`;
      this.sliderFrame.style.transition = `all ${this.config.duration}ms ${this.config.easing}`;
      this.updateAfterDrag();
      this.clearDrag();
    }
  }

  /**
   * @description Update after removing, prepending or appending items.
   * @method updateFrame
   * @returns {void}
   */
  updateFrame() {
    // Create frame and apply styling
    this.sliderFrame = document.createElement('div');
    this.sliderFrame.style.width = `${this.selectorWidth / this.perPage * this.innerElements.length}px`;
    this.sliderFrame.style.webkitTransition = this.currentSlide === this.rightLimit || this.leftLimit ? 'all 0ms none' : `all ${this.config.duration}ms ${this.config.easing}`;
    this.sliderFrame.style.transition = `all ${this.config.duration}ms ${this.config.easing}`;
    if (this.config.draggable) {
      this.selector.style.cursor = '-webkit-grab';
    }

    // Create a document fragment to put slides into it
    const docFragment = document.createDocumentFragment();

    // Loop through the slides, add styling and add them to document fragment
    for (let i = 0; i < this.innerElements.length; i += 1) {
      const elementContainer = document.createElement('div');
      elementContainer.style.cssFloat = 'left';
      elementContainer.style.float = 'left';
      elementContainer.style.width = `${100 / this.innerElements.length}%`;
      elementContainer.appendChild(this.innerElements[i]);
      docFragment.appendChild(elementContainer);
    }

    // Add fragment to the frame
    this.sliderFrame.appendChild(docFragment);

    // Clear selector (just in case something is there) and insert a frame
    this.selector.innerHTML = '';
    this.selector.appendChild(this.sliderFrame);

    // Go to currently active slide after initial build
    this.slideToCurrent();

    // update tabIndex and aria-attributes for visible and hidden slide elements
    this.updateSliderADA();
  }

  /**
   * @descriptino Remove item from carousel.
   * @method remove
   * @param {number} index - Item index to remove.
   * @param {function} callback - Optional callback to call after remove.
   * @returns {void}
   */
  remove(index, callback) {
    if (index < 0 || index > this.innerElements.length) {
      throw new Error('Item to remove doesn\'t exist 😭');
    }
    this.innerElements.splice(index, 1);

    // Avoide shifting content
    this.currentSlide = index < this.currentSlide ? this.currentSlide - 1 : this.currentSlide;
    this.updateFrame();
    if (callback) {
      callback.call(this);
    }
  }

  /**
   * @description Insert item to carousel at particular index.
   * @method insert
   * @param {HTMLNode} item - Item to insert.
   * @param {number} index - Index of new new item insertion.
   * @param {function} callback - Optional callback to call after insert.
   * @returns {void}
   */
  insert(item, index, callback) {
    if (index < 0 || index > this.innerElements.length + 1) {
      throw new Error('Unable to inset it at this index 😭');
    }
    if (this.innerElements.indexOf(item) !== -1) {
      throw new Error('The same item in a carousel? Really? Nope 😭');
    }
    this.innerElements.splice(index, 0, item);

    // Avoide shifting content
    this.currentSlide = index <= this.currentSlide ? this.currentSlide + 1 : this.currentSlide;
    this.updateFrame();
    if (callback) {
      callback.call(this);
    }
  }

  /**
   * @description Prepernd item to carousel.
   * @method prepend
   * @param {HTMLNode} item - Item to prepend.
   * @param {function} callback - Optional callback to call after prepend.
   * @returns {void}
   */
  prepend(item, callback) {
    this.insert(item, 0);
    if (callback) {
      callback.call(this);
    }
  }

  /**
   * @description Append item to carousel.
   * @method append
   * @param {HTMLNode} item - Item to append.
   * @param {function} callback - Optional callback to call after append.
   * @returns {void}
   */
  append(item, callback) {
    this.insert(item, this.innerElements.length + 1);
    if (callback) {
      callback.call(this);
    }
  }

  /**
   * @description Updates slide content with new slides
   * @method updateSlides
   * @param {HTMLNodeList} slides - slides to update slider with
   * @returns {void}
   */
  updateSlides(slides) {
    this.innerElements = slides;
  }

  /**
   * @description Removes listeners and optionally restores to initial markup
   * @method destroy
   * @param {boolean} restoreMarkup - Determinants about restoring an initial markup.
   * @param {function} callback - Optional callback function.
   * @returns {void}
   */
  destroy(restoreMarkup = false, callback) {
    window.removeEventListener('resize', this.resizeHandler);
    this.selector.style.cursor = 'auto';
    this.selector.removeEventListener('touchstart', this.touchstartHandler);
    this.selector.removeEventListener('touchend', this.touchendHandler);
    this.selector.removeEventListener('touchmove', this.touchmoveHandler);
    this.selector.removeEventListener('mousedown', this.mousedownHandler);
    this.selector.removeEventListener('mouseup', this.mouseupHandler);
    this.selector.removeEventListener('mouseleave', this.mouseleaveHandler);
    this.selector.removeEventListener('mousemove', this.mousemoveHandler);
    if (restoreMarkup) {
      const slides = document.createDocumentFragment();
      for (let i = 0; i < this.originalElements.length; i += 1) {
        // reset tabIndex of original elements that may have been changed inside
        const link = this.originalElements[i].querySelector('a');
        if (link) {
          Object.assign(link, {
            tabIndex: 0
          });
        }
        slides.appendChild(this.originalElements[i]);
      }
      this.selector.innerHTML = '';
      // we should remove the elements that have a class of clone and then insert the slides
      this.selector.appendChild(slides);
    }
    // set innerElements to empty array to avoid collecting duplicates
    // - will be restored if user goes to large and then back to small
    this.innerElements = [];
    if (callback) {
      callback.call(this);
    }
  }
}

export { Siema as default };
